import { EventSnapshot, Market, Outcome } from '../api/types';

/**
 * Extract the primary specifier/attr value from a market for display.
 * E.g. { total: "2.5" } → "2,5"   |   { hcp: "0:1" } → "0:1"   |   { goalnr: "1" } → "1"
 * Returns null if no specifiers exist.
 */
export function getMarketAttr(market: Market): string | null {
  if (!market.specifiers || Object.keys(market.specifiers).length === 0) return null;
  // Pick the most meaningful specifier value (total, hcp, goalnr, setnr, etc.)
  const priority = ['total', 'hcp', 'goalnr', 'setnr', 'gamenr', 'from', 'score'];
  for (const key of priority) {
    if (market.specifiers[key] != null) {
      return String(market.specifiers[key]).replace('.', ',');
    }
  }
  // Fallback: return the first specifier value
  const firstVal = Object.values(market.specifiers)[0];
  return firstVal != null ? String(firstVal).replace('.', ',') : null;
}

/**
 * Dynamically format the bet slip label with the market's specifier attribute.
 * E.g., "Über 0,5" with market total=2.5 becomes "Über 2,5"
 * E.g., "Handicap 1" with market hcp=0:1 becomes "Handicap 1 (0:1)"
 */
export function formatTippLabelWithAttr(label: string, market: Market): string {
  const attr = getMarketAttr(market);
  if (!attr) return label;

  if (label.includes('0,5')) {
    return label.replace('0,5', attr);
  }

  if (!label.includes(attr)) {
    return `${label} (${attr})`;
  }

  return label;
}


export function find1x2Market(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '1' && mk.outcomes.length >= 3 && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1');
  const draw = m.outcomes.find((o) => o.outcome_id === '2');
  const away = m.outcomes.find((o) => o.outcome_id === '3');
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}

export function findRestzeitGeneric(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => (mk.market_id === '2' || mk.market_id === '73' || mk.market_id === '7') && mk.outcomes.length >= 3 && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1');
  const draw = m.outcomes.find((o) => o.outcome_id === '2');
  const away = m.outcomes.find((o) => o.outcome_id === '3');
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}

export function find1stHalfRestzeitGeneric(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '61' && mk.outcomes.length >= 3 && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1');
  const draw = m.outcomes.find((o) => o.outcome_id === '2');
  const away = m.outcomes.find((o) => o.outcome_id === '3');
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}


export function findOUGeneric(markets: Market[], is1stHalf = false, event?: EventSnapshot) {
  const targetId = is1stHalf ? '68' : '18';

  let targetLine: string | undefined;
  if (event && (event.event.sport_name === 'Soccer' || event.event.sport_id === 'sr:sport:1')) {
    if (event.scope === 'pre' || (event.status as any) === 'not_started') {
      targetLine = '2.5';
    } else {
      const homeScore = event.status?.home_score ?? 0;
      const awayScore = event.status?.away_score ?? 0;
      const totalGoals = homeScore + awayScore;
      const clockStr = event.status?.clock || '';
      const minute = parseInt(clockStr.split(':')[0], 10) || 0;

      if (minute < 30) {
        targetLine = (totalGoals + 2.5).toFixed(1);
      } else {
        targetLine = (totalGoals + 1.5).toFixed(1);
      }
    }
  }

  let m = targetLine !== undefined ? markets.find((mk) => (
    mk.market_id === targetId ||
    (!is1stHalf && (mk.market_id === '189' || mk.market_id === '204' || mk.market_id === '225' || mk.market_id === '238' || mk.market_id === '258' || mk.market_id === '314'))
  ) && mk.status !== -1 && mk.specifiers?.total === targetLine) : undefined;

  // Fallback: if specific line not found or not provided, return the first active market
  if (!m) {
    m = markets.find((mk) => (
      mk.market_id === targetId ||
      (!is1stHalf && (mk.market_id === '189' || mk.market_id === '204' || mk.market_id === '225' || mk.market_id === '238' || mk.market_id === '258' || mk.market_id === '314'))
    ) && mk.status !== -1
    );
  }

  if (!m) return null;
  const over = m.outcomes.find((o) => (o.name.toLowerCase().includes('over') || o.name.startsWith('Über')));
  const under = m.outcomes.find((o) => (o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')));
  const overOutcome = over || m.outcomes[0];
  const underOutcome = under || m.outcomes[1];
  if (!overOutcome || !underOutcome) return null;
  return { market: m, over: overOutcome, under: underOutcome };
}

export function findNextGoal(markets: Market[]): { home: Outcome; none: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => (mk.market_id === '71' || mk.market_id === '8') && mk.status !== -1
  );
  if (!m) return null;
  let home = m.outcomes.find((o) => (o.outcome_id === '1' || o.outcome_id === '6' || o.name.toLowerCase().includes('home')));
  let none = m.outcomes.find((o) => (o.outcome_id === 'X' || o.outcome_id === '7' || o.outcome_id === '21' || o.name.toLowerCase() === 'no' || o.name.toLowerCase() === 'no goal' || o.name.toLowerCase().includes('kein') || o.name.toLowerCase().includes('none') || o.name.toLowerCase() === 'x'));
  let away = m.outcomes.find((o) => (o.outcome_id === '2' || o.outcome_id === '3' || o.outcome_id === '8' || o.name.toLowerCase().includes('away')) && o.outcome_id !== none?.outcome_id);

  if (!home) home = m.outcomes[0];
  if (!none) none = m.outcomes[1];
  if (!away) away = m.outcomes.length > 2 ? m.outcomes[2] : undefined;

  if (!home || !none || !away) return null;
  return { home, none, away, market: m };
}

export function find1stHalfNextGoal(markets: Market[]): { home: Outcome; none: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '62' && mk.status !== -1
  );
  if (!m) return null;
  let home = m.outcomes.find((o) => (o.outcome_id === '1' || o.outcome_id === '6' || o.name.toLowerCase().includes('home')));
  let none = m.outcomes.find((o) => (o.outcome_id === 'X' || o.outcome_id === '7' || o.outcome_id === '21' || o.name.toLowerCase() === 'no' || o.name.toLowerCase() === 'no goal' || o.name.toLowerCase().includes('kein') || o.name.toLowerCase().includes('none') || o.name.toLowerCase() === 'x'));
  let away = m.outcomes.find((o) => (o.outcome_id === '2' || o.outcome_id === '3' || o.outcome_id === '8' || o.name.toLowerCase().includes('away')) && o.outcome_id !== none?.outcome_id);

  if (!home) home = m.outcomes[0];
  if (!none) none = m.outcomes[1];
  if (!away) away = m.outcomes.length > 2 ? m.outcomes[2] : undefined;

  if (!home || !none || !away) return null;
  return { home, none, away, market: m };
}

export function findHandicap(markets: Market[], is1stHalf = false): { o1: Outcome; o2: Outcome; o3?: Outcome; market: Market } | null {
  const targetIds = is1stHalf ? ['65', '66'] : ['14', '16', '11', '187', '223', '237', '246', '254', '256'];
  for (const targetId of targetIds) {
    const m = markets.find((mk) => mk.market_id === targetId && mk.outcomes.length >= 2 && mk.status !== -1);
    if (!m) continue;
    // Could be 3-way or 2-way handicap
    const o1 = m.outcomes.find((o) => o.outcome_id === '1') || m.outcomes[0];
    const o2 = m.outcomes.find((o) => (o.outcome_id === '3' || o.outcome_id === '2')) || m.outcomes[1]; // Usually 3 is away for 3-way handicap, 2 is away for 2-way
    const o3 = m.outcomes.length >= 3 ? (m.outcomes.find((o) => o.outcome_id === '2') || m.outcomes[2]) : undefined; // Draw might be outcome_id '2'
    if (!o1 || !o2) continue;
    return { o1, o2, o3, market: m };
  }
  return null;
}

export function findSatzHandicap(markets: Market[]): { o1: Outcome; o2: Outcome; o3?: Outcome; market: Market } | null {
  const m = markets.find((mk) => (mk.market_id === '155' || mk.market_id === '203' || mk.market_id === '246') && mk.outcomes.length >= 2 && mk.status !== -1);
  if (!m) return null;
  const o1 = m.outcomes.find((o) => o.outcome_id === '1') || m.outcomes[0];
  const o2 = m.outcomes.find((o) => (o.outcome_id === '3' || o.outcome_id === '2')) || m.outcomes[1];
  const o3 = m.outcomes.length >= 3 ? (m.outcomes.find((o) => o.outcome_id === '2') || m.outcomes[2]) : undefined;
  if (!o1 || !o2) return null;
  return { o1, o2, o3, market: m };
}

export function findOddEven(markets: Market[], is1stHalf = false): { odd: Outcome; even: Outcome; market: Market } | null {
  const targetIds = is1stHalf ? ['74'] : ['26', '229', '198', '205'];
  const m = markets.find((mk) => targetIds.includes(mk.market_id) && mk.outcomes.length >= 2 && mk.status !== -1);
  if (!m) return null;
  const odd = m.outcomes.find((o) => o.outcome_id === '70' || o.name.toLowerCase().includes('odd') || o.name.toLowerCase() === 'tek') || m.outcomes[0];
  const even = m.outcomes.find((o) => o.outcome_id === '72' || o.name.toLowerCase().includes('even') || o.name.toLowerCase() === 'çift') || m.outcomes[1];
  if (!odd || !even) return null;
  return { odd, even, market: m };
}

export function findBTTSGeneric(markets: Market[], is1stHalf = false) {
  const targetId = is1stHalf ? '75' : '29';
  const m = markets.find((mk) => mk.market_id === targetId && mk.status !== -1
  );
  if (!m) return null;
  const yes = m.outcomes.find((o) => (o.outcome_id === '74' || o.name.toLowerCase() === 'yes' || o.name.toLowerCase() === 'ja'));
  const no = m.outcomes.find((o) => (o.outcome_id === '76' || o.name.toLowerCase() === 'no' || o.name.toLowerCase() === 'nein'));
  if (!yes || !no) return null;
  return { market: m, yes, no };
}

export function findDCGeneric(markets: Market[], is1stHalf = false) {
  const targetId = is1stHalf ? '63' : '10';
  const m = markets.find((mk) => mk.market_id === targetId && mk.status !== -1
  );
  if (!m) return null;
  const o1x = m.outcomes.find((o) => (o.outcome_id === '9' || o.name.toLowerCase().includes('1x')));
  const o12 = m.outcomes.find((o) => (o.outcome_id === '10' || o.name.toLowerCase().includes('12')));
  const ox2 = m.outcomes.find((o) => (o.outcome_id === '11' || o.name.toLowerCase().includes('x2')));
  if (!o1x || !o12 || !ox2) return null;
  return { market: m, o1x, o12, ox2 };
}

export function findH2H(markets: Market[], is1stHalf = false): { home: Outcome; away: Outcome; market: Market } | null {
  const targetIds = is1stHalf ? ['61', '66'] : ['11', '21', '218', '251', '186', '2', '219'];
  const m = markets.find((mk) => targetIds.includes(mk.market_id) && mk.outcomes.length >= 2 && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1' || o.outcome_id === '78' || o.name?.toLowerCase() === '1') || m.outcomes[0];
  const away = m.outcomes.find((o) => o.outcome_id === '3' || o.outcome_id === '2' || o.outcome_id === '79' || o.name?.toLowerCase() === '2') || m.outcomes[1];
  if (!home || !away) return null;
  return { home, away, market: m };
}

export function findSatzSieger(markets: Market[]): { home: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => (mk.market_id === '148' || mk.market_id === '202' || mk.market_id === '245') && mk.outcomes.length >= 2 && mk.status !== -1);
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1' || o.outcome_id === '78' || o.name?.toLowerCase() === '1') || m.outcomes[0];
  const away = m.outcomes.find((o) => o.outcome_id === '3' || o.outcome_id === '2' || o.outcome_id === '79' || o.name?.toLowerCase() === '2') || m.outcomes[1];
  if (!home || !away) return null;
  return { home, away, market: m };
}

export function find1stHalfMarket(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const targetIds = ['60', '61', '530', '1179', '6', '436'];
  const m = markets.find((mk) => targetIds.includes(mk.market_id) && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => (o.outcome_id === '1' || o.outcome_id === '6' || o.outcome_id === '14' || o.name === '1' || o.name.toLowerCase() === 'home'));
  const draw = m.outcomes.find((o) => (o.outcome_id === '2' || o.outcome_id === '7' || o.outcome_id === '15' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x' || o.name.toLowerCase() === 'keines' || o.name.toLowerCase() === 'none'));
  const away = m.outcomes.find((o) => (o.outcome_id === '3' || o.outcome_id === '8' || o.outcome_id === '16' || o.name === '2' || o.name.toLowerCase() === 'away'));
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}

export function findQuarter1x2(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '235' && mk.status !== -1);
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1') || m.outcomes[0];
  const draw = m.outcomes.find((o) => o.outcome_id === '2') || m.outcomes[1];
  const away = m.outcomes.find((o) => o.outcome_id === '3') || m.outcomes[2];
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}

export function findQuarterTotal(markets: Market[]): { over: Outcome; under: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '236' && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über')) || m.outcomes[0];
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')) || m.outcomes[1];
  if (!over || !under) return null;
  return { over, under, market: m };
}

export function findPointsTotal(markets: Market[]): { over: Outcome; under: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '238' && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über')) || m.outcomes[0];
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')) || m.outcomes[1];
  if (!over || !under) return null;
  return { over, under, market: m };
}

export function findSatzTotal(markets: Market[]): { over: Outcome; under: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '247' && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über')) || m.outcomes[0];
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')) || m.outcomes[1];
  if (!over || !under) return null;
  return { over, under, market: m };
}

export function findTeamTotal(markets: Market[], isHome: boolean, is1stHalf = false): { over: Outcome; under: Outcome; market: Market } | null {
  const targetIds = isHome
    ? (is1stHalf ? ['69'] : ['19', '227', '260'])
    : (is1stHalf ? ['70'] : ['20', '228', '261']);
  const m = markets.find((mk) => targetIds.includes(mk.market_id) && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über')) || m.outcomes[0];
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')) || m.outcomes[1];
  if (!over || !under) return null;
  return { over, under, market: m };
}

export function findOvertimeYesNo(markets: Market[]): { yes: Outcome; no: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '220' && mk.status !== -1);
  if (!m) return null;
  const yes = m.outcomes.find((o) => o.outcome_id === '74' || o.name.toLowerCase() === 'yes' || o.name.toLowerCase() === 'ja') || m.outcomes[0];
  const no = m.outcomes.find((o) => o.outcome_id === '76' || o.name.toLowerCase() === 'no' || o.name.toLowerCase() === 'nein') || m.outcomes[1];
  if (!yes || !no) return null;
  return { yes, no, market: m };
}

export function find2ndHalfHandicap(markets: Market[]): { o1: Outcome; o2: Outcome; o3?: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '231' && mk.status !== -1);
  if (!m) return null;
  const o1 = m.outcomes.find((o) => o.outcome_id === '1714') || m.outcomes[0];
  const o2 = m.outcomes.find((o) => o.outcome_id === '1715') || m.outcomes[1];
  if (!o1 || !o2) return null;
  return { o1, o2, market: m };
}

export function find2ndHalfTotal(markets: Market[]): { over: Outcome; under: Outcome; market: Market } | null {
  const m = markets.find((mk) => mk.market_id === '232' && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über')) || m.outcomes[0];
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter')) || m.outcomes[1];
  if (!over || !under) return null;
  return { over, under, market: m };
}

export function isFilterAvailable(rawFilter: string | number, markets: Market[] | undefined): boolean {
  if (!markets || markets.length === 0) return false;
  const filter = getLayoutGroupForMarketId(rawFilter);
  switch (filter) {
    case 'Tipp':
      return find1x2Market(markets) !== null;
    case '1. Halbzeit':
      return find1stHalfMarket(markets) !== null;
    case 'Tipp Restzeit':
      return findRestzeitGeneric(markets) !== null;
    case '1.Hz Tipp Restzeit':
      return find1stHalfRestzeitGeneric(markets) !== null;
    case 'Über/Unter':
      return findOUGeneric(markets) !== null;
    case 'Nächstes Tor':
      return findNextGoal(markets) !== null;
    case 'Handicap':
      return findHandicap(markets) !== null;
    case 'Satz-Handicap':
      return findSatzHandicap(markets) !== null;
    case 'Beide Teams treffen':
      return findBTTSGeneric(markets) !== null;
    case 'Halbzeit-Über/Unter':
      return findOUGeneric(markets, true) !== null;
    case 'Doppelte Chance':
      return findDCGeneric(markets) !== null;
    case 'Head-To-Head':
    case 'Sieger':
    case 'Draw No Bet':
      return findH2H(markets) !== null;
    case 'Satz Sieger':
      return findSatzSieger(markets) !== null;
    case 'Gerade/Ungerade':
      return findOddEven(markets) !== null;
    case 'Heim Über/Unter':
      return findTeamTotal(markets, true) !== null;
    case 'Auswärts Über/Unter':
      return findTeamTotal(markets, false) !== null;
    case 'Verlängerung Ja/Nein':
      return findOvertimeYesNo(markets) !== null;
    case '2. Halbzeit Handicap':
      return find2ndHalfHandicap(markets) !== null;
    case '2. Halbzeit Über/Unter':
      return find2ndHalfTotal(markets) !== null;
    case 'Viertel 1x2':
      return findQuarter1x2(markets) !== null;
    case 'Viertel Über/Unter':
      return findQuarterTotal(markets) !== null;
    case '1.Hz Doppelte Chance':
      return findDCGeneric(markets, true) !== null;
    case '1.Hz Handicap':
      return findHandicap(markets, true) !== null;
    case '1.Hz Heim Über/Unter':
      return findTeamTotal(markets, true, true) !== null;
    case '1.Hz Auswärts Über/Unter':
      return findTeamTotal(markets, false, true) !== null;
    case '1.Hz Gerade/Ungerade':
      return findOddEven(markets, true) !== null;
    case 'Toplam Sayı Alt/Üst':
      return findPointsTotal(markets) !== null;
    case 'Set Toplam Sayı Alt/Üst':
      return findSatzTotal(markets) !== null;
    default:
      return false;
  }
}

export function getLayoutGroupForMarketId(id: number | string): string {
  const numId = Number(id);
  const layoutMap: Record<number, string> = {
    1: 'Tipp', 60: '1. Halbzeit', 7: 'Tipp Restzeit', 73: '1.Hz Tipp Restzeit', 
    8: 'Nächstes Tor', 10: 'Doppelte Chance', 62: '1.Hz Doppelte Chance',
    11: 'Draw No Bet', 29: 'Beide Teams treffen', 28: 'Gerade/Ungerade', 65: '1.Hz Gerade/Ungerade',
    14: 'Handicap', 61: '1.Hz Handicap', 121: '2. Halbzeit Handicap',
    18: 'Über/Unter', 21: 'Halbzeit-Über/Unter', 122: '2. Halbzeit Über/Unter',
    19: 'Heim Über/Unter', 63: '1.Hz Heim Über/Unter',
    20: 'Auswärts Über/Unter', 64: '1.Hz Auswärts Über/Unter',
    50: 'Head-To-Head', 220: 'Verlängerung Ja/Nein',
    341: 'Viertel 1x2', 342: 'Viertel Über/Unter',
    
    // Abstracted sports (Tennis, Basketball, etc)
    251: 'Sieger', 186: 'Sieger', 219: 'Sieger',
    148: 'Satz Sieger', 202: 'Satz Sieger',
    187: 'Handicap', 237: 'Handicap', 223: 'Handicap',
    155: 'Satz-Handicap', 203: 'Satz-Handicap', 246: 'Satz-Handicap',
    189: 'Über/Unter', 258: 'Über/Unter', 225: 'Über/Unter', 238: 'Über/Unter',
    204: 'Satz Über/Unter', 310: 'Satz Über/Unter',
  };
  return layoutMap[numId] || String(id);
}

export function getMarketOptionsForSport(sportId: string, isLive = false): (number | string)[] {
  switch (sportId) {
    case 'Favorites':
      return [1, 251, 18, 10, 14, 29, 11, 60, 155];
    case 'sr:sport:1': // Soccer
    case 'Soccer':
      return isLive
        ? [1, 7, 8, 18, 29, 60, 10, 11, 14, 68, 75] 
        : [1, 18, 14, 29, 68, 10, 11, 8, 60, 75];
    case 'sr:sport:374': // eSoccer
    case 'eSoccer':
      return isLive
        ? [1, 18, 14, 60, 11, 7, 19, 20, 61]
        : [1, 18, 14, 60, 11, 19, 20, 61];
    case 'sr:sport:2': // Basketball
    case 'Basketball':
      return [219, 225, 28, 341, 342, 223, 60, 19, 20, 121, 122, 220];
    case 'sr:sport:5': // Tennis
    case 'Tennis':
      return [186, 187, 189, 148, 155];
    case 'sr:sport:4': // Ice Hockey
    case 'Ice Hockey':
      return [1, 251, 18, 14, 60, 29, 61, 62, 63, 11, 10, 8];
    case 'sr:sport:3': // Baseball
    case 'Baseball':
      return [219, 225, 223];
    case 'sr:sport:23': // Volleyball
    case 'Volleyball':
      return [219, 148, 189, 187];
    case 'sr:sport:12': // Rugby
    case 'Rugby':
      return [1, 18, 14, 10, 11, 8];
    default:
      return [1, 18, 14];
  }
}

export function getMarketKeyFromId(sportId: string, marketId: string | number): string | undefined {
  const idStr = String(marketId);
  if (sportId === 'sr:sport:1' || sportId === 'Soccer') {
    const map: Record<string, string> = {
      '1': 'three_way',
      '18': 'total',
      '14': 'handicap',
      '29': 'btts',
      '68': 'half_total',
      '10': 'double_chance',
      '11': 'draw_no_bet',
      '8': 'first_team_to_score',
      '60': 'half_winner',
      '75': 'half_btts'
    };
    return map[idStr];
  }
  if (sportId === 'sr:sport:2' || sportId === 'Basketball') {
    const map: Record<string, string> = {
      '219': 'two_way',
      '225': 'total_points',
      '223': 'handicap',
      '60': 'half_winner'
    };
    return map[idStr];
  }
  if (sportId === 'sr:sport:5' || sportId === 'Tennis') {
    const map: Record<string, string> = {
      '186': 'match_winner',
      '187': 'set_winner',
      '189': 'total_sets',
      '148': 'handicap',
      '155': 'tiebreak_set'
    };
    return map[idStr];
  }
  return undefined;
}

export function getIdFromMarketKey(sportId: string, marketKey: string): number | undefined {
  if (sportId === 'sr:sport:1' || sportId === 'Soccer') {
    const map: Record<string, number> = {
      'three_way': 1,
      'total': 18,
      'handicap': 14,
      'btts': 29,
      'half_total': 68,
      'double_chance': 10,
      'draw_no_bet': 11,
      'first_team_to_score': 8,
      'half_winner': 60,
      'half_btts': 75
    };
    return map[marketKey];
  }
  if (sportId === 'sr:sport:2' || sportId === 'Basketball') {
    const map: Record<string, number> = {
      'two_way': 219,
      'total_points': 225,
      'handicap': 223,
      'half_winner': 60
    };
    return map[marketKey];
  }
  if (sportId === 'sr:sport:5' || sportId === 'Tennis') {
    const map: Record<string, number> = {
      'match_winner': 186,
      'set_winner': 187,
      'total_sets': 189,
      'handicap': 148,
      'tiebreak_set': 155
    };
    return map[marketKey];
  }
  return undefined;
}
